// アシスタント機能 (spec §4.4): 事前登録した定型文をワンクリックで Zoom Chat に投下する。
// MVP: in-client から最低限の登録 / 削除 / 投下のみ。リッチな編集 UI は web app (M9 以降)。
//
// 投下は backend 経由で Zoom Chat API を叩く (spec §6.5)。AI 自動投稿は spec §15 で禁止。

import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import { t, type Locale, type MessageKey } from '@solo-webinar-copilot/i18n';
import type {
  QuickMessage,
  QuickMessageCategory,
} from '@solo-webinar-copilot/shared';
import {
  createQuickMessage,
  deleteQuickMessage,
  listQuickMessages,
  postQuickMessage,
} from '../lib/api-client.ts';
import type { AppContext } from '../lib/zoom-sdk.ts';

const CATEGORIES: readonly QuickMessageCategory[] = [
  'survey',
  'announcement',
  'message',
  'custom',
];

const CATEGORY_LABEL_KEY: Record<QuickMessageCategory, MessageKey> = {
  survey: 'in_client.host.quick_messages.category.survey',
  announcement: 'in_client.host.quick_messages.category.announcement',
  message: 'in_client.host.quick_messages.category.message',
  custom: 'in_client.host.quick_messages.category.custom',
};

export interface QuickMessagesProps {
  ctx: AppContext;
  locale: Locale;
}

export function QuickMessages({ ctx, locale }: QuickMessagesProps): ReactElement {
  const [items, setItems] = useState<QuickMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [postedId, setPostedId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const r = await listQuickMessages(ctx);
    if (r.ok) {
      setItems(r.items);
      setError(null);
    } else {
      setError(r.error);
    }
    setLoading(false);
  }, [ctx]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handlePost = async (id: string) => {
    setError(null);
    const r = await postQuickMessage(ctx, id);
    if (r.ok) {
      setPostedId(id);
    } else {
      setError(r.error);
    }
  };

  const handleDelete = async (id: string) => {
    if (
      typeof window !== 'undefined' &&
      !window.confirm(t('in_client.host.quick_messages.confirm_delete', locale))
    ) {
      return;
    }
    setError(null);
    const r = await deleteQuickMessage(ctx, id);
    if (r.ok) {
      setItems((prev) => prev.filter((x) => x.id !== id));
    } else {
      setError(r.error);
    }
  };

  return (
    <section data-testid="quick-messages">
      <h2>{t('in_client.host.quick_messages.title', locale)}</h2>
      {error ? (
        <p role="alert" data-testid="qm-error">
          {t('in_client.error.network', locale)}: {error}
        </p>
      ) : null}
      {loading ? (
        <p data-testid="qm-loading">…</p>
      ) : items.length === 0 ? (
        <p data-testid="qm-empty">{t('in_client.host.quick_messages.empty', locale)}</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {items.map((m) => (
            <li
              key={m.id}
              data-testid={`qm-item-${m.id}`}
              style={{ display: 'flex', gap: 8, marginBottom: 6 }}
            >
              <button
                type="button"
                onClick={() => void handlePost(m.id)}
                data-testid={`qm-post-${m.id}`}
                style={{ flex: 1, padding: '10px 8px', textAlign: 'left' }}
              >
                <small style={{ opacity: 0.6 }}>
                  [{t(CATEGORY_LABEL_KEY[m.category], locale)}]
                </small>{' '}
                {m.label}
              </button>
              <button
                type="button"
                onClick={() => void handleDelete(m.id)}
                data-testid={`qm-delete-${m.id}`}
                aria-label={t('in_client.host.quick_messages.delete', locale)}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
      {postedId ? (
        <p data-testid="qm-posted">
          {t('in_client.host.quick_messages.posted', locale)}
        </p>
      ) : null}

      {showForm ? (
        <QuickMessageForm
          locale={locale}
          onCancel={() => setShowForm(false)}
          onSubmit={async (draft) => {
            const r = await createQuickMessage(ctx, draft);
            if (r.ok) {
              setItems((prev) => [...prev, r.item]);
              setShowForm(false);
              setError(null);
            } else {
              setError(r.error);
            }
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          data-testid="qm-add-button"
        >
          {t('in_client.host.quick_messages.add', locale)}
        </button>
      )}
    </section>
  );
}

interface QuickMessageFormProps {
  locale: Locale;
  onCancel: () => void;
  onSubmit: (draft: {
    label: string;
    content: string;
    category: QuickMessageCategory;
  }) => Promise<void>;
}

function QuickMessageForm({
  locale,
  onCancel,
  onSubmit,
}: QuickMessageFormProps): ReactElement {
  const [label, setLabel] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<QuickMessageCategory>('message');
  const [submitting, setSubmitting] = useState(false);

  const handle = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!label.trim() || !content.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit({ label: label.trim(), content: content.trim(), category });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handle} data-testid="qm-form">
      <div>
        <label>
          {t('in_client.host.quick_messages.label', locale)}
          <input
            type="text"
            value={label}
            maxLength={60}
            onChange={(e) => setLabel(e.currentTarget.value)}
            data-testid="qm-form-label"
            required
          />
        </label>
      </div>
      <div>
        <label>
          {t('in_client.host.quick_messages.content', locale)}
          <textarea
            value={content}
            maxLength={2000}
            onChange={(e) => setContent(e.currentTarget.value)}
            data-testid="qm-form-content"
            required
          />
        </label>
      </div>
      <div>
        <label>
          {t('in_client.host.quick_messages.category', locale)}
          <select
            value={category}
            onChange={(e) =>
              setCategory(e.currentTarget.value as QuickMessageCategory)
            }
            data-testid="qm-form-category"
          >
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {t(CATEGORY_LABEL_KEY[cat], locale)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div>
        <button type="submit" disabled={submitting} data-testid="qm-form-save">
          {t('in_client.host.quick_messages.save', locale)}
        </button>
        <button type="button" onClick={onCancel} data-testid="qm-form-cancel">
          {t('in_client.host.quick_messages.cancel', locale)}
        </button>
      </div>
    </form>
  );
}
